import { useMemo } from 'react';
import { useTheme } from '../../shared/theme/ThemeContext';
import {
  getVisibleAnalysisNavIcons,
  RAIL_TAB_META,
  RailToolIcon,
  type MapToolRailIcon,
} from '../../shared/components/MapToolRail';
import sidebarLogo from '../../../assets/digisaka-sidebar-logo.png';
import { DRONE_PROCESSING_COMING_SOON } from '../../drone-processing/comingSoon';
import { isFieldServiceSensorNavSection } from '../../carbon-projects/fieldServicesNav';

/** Same widths as the Digisaka app `Aside.vue` (expanded / icon rail). */
export const NAV_SIDEBAR_EXPANDED_PX = 272;
export const NAV_SIDEBAR_COLLAPSED_PX = 72;

export const NAV_SIDEBAR_STORAGE_KEY = 'digisaka.navSidebarExpanded';

/** Menu wording and the short rail caption for each tool. */
const NAV_ITEM_COPY: Record<MapToolRailIcon, { label: string; short: string }> = {
  farm: { label: 'Farm Analysis', short: 'Farm' },
  forecast: { label: 'Rice Yield Forecast', short: 'Yield' },
  multiTemporal: { label: 'Multi-temporal maps', short: 'Maps' },
  droneData: { label: 'Drone', short: 'Drone' },
  fieldServices: { label: 'Field services', short: 'Services' },
  cropArea: { label: 'Crop Type Maps', short: 'Crops' },
  disasterLossDamage: { label: 'Disaster Loss and Damages', short: 'Loss' },
  lowCarbon: { label: 'Low Carbon Rice', short: 'Carbon' },
  treeDetection: { label: 'AI Detection', short: 'AI' },
};

/** Page title and one-line description shown in the top bar for the open tool. */
export const NAV_PAGE_COPY: Record<MapToolRailIcon, { title: string; subtitle: string }> = {
  farm: {
    title: 'Farm Analysis',
    subtitle: 'Explore and manage digitized farm boundaries',
  },
  forecast: {
    title: 'Rice Yield Forecast',
    subtitle: 'Seasonal yield estimates across your areas',
  },
  multiTemporal: {
    title: 'Multi-temporal maps',
    subtitle: 'Compare conditions across dates and seasons',
  },
  droneData: {
    title: 'Drone',
    subtitle: 'Process a flight, or open sample drone imagery',
  },
  fieldServices: {
    title: 'Field services',
    subtitle: 'Process a flight, open sample imagery, or request sensors',
  },
  cropArea: {
    title: 'Crop Type Maps',
    subtitle: 'See where each crop is grown',
  },
  disasterLossDamage: {
    title: 'Disaster Loss and Damages',
    subtitle: 'Typhoon and drought loss and damage estimates',
  },
  lowCarbon: {
    title: 'Low Carbon Rice',
    subtitle: 'AWD maps and rice straw burning',
  },
  treeDetection: {
    title: 'AI Detection',
    subtitle: 'Trees, crowns and field boundaries',
  },
};

/** One layer or tab listed under an open tool, like "Low Carbon Credits" under "Sustainability". */
export interface NavSubItem {
  id: string;
  label: string;
  /** Shows an "About this layer" button on the row. */
  hasInfo?: boolean;
}

interface DashboardNavSidebarProps {
  expanded: boolean;
  onToggleExpanded: () => void;
  activeIcon: MapToolRailIcon | null;
  onIconClick: (icon: MapToolRailIcon) => void;
  onOpenSettings: () => void;
  /** Settings is a page, so the menu marks it while it is open. */
  settingsActive?: boolean;
  allowedEntitlementSet?: Set<string> | null;
  /** True while the active tool is open, so its layers can be listed under it. */
  toolOpen?: boolean;
  subItems?: NavSubItem[];
  activeSubItemId?: string | null;
  onSubItemClick?: (id: string) => void;
  /** Clicking the layer that is already on closes it. */
  onSubItemClose?: () => void;
  droneProcessingActive?: boolean;
  onOpenDroneProcessing?: () => void;
  /** Carbon Project Developer home. Hidden for other roles. */
  showCarbonProjects?: boolean;
  carbonProjectsActive?: boolean;
  onOpenCarbonProjects?: () => void;
}

function entitlementKey(set: Set<string> | null | undefined): string {
  if (set == null) return '*';
  return [...set].sort().join('|');
}

export default function DashboardNavSidebar({
  expanded,
  onToggleExpanded,
  activeIcon,
  onIconClick,
  onOpenSettings,
  settingsActive = false,
  allowedEntitlementSet = null,
  toolOpen = false,
  subItems = [],
  activeSubItemId = null,
  onSubItemClick,
  onSubItemClose,
  droneProcessingActive = false,
  onOpenDroneProcessing,
  showCarbonProjects = false,
  carbonProjectsActive = false,
  onOpenCarbonProjects,
}: DashboardNavSidebarProps) {
  const { isDark } = useTheme();
  const entitlementSig = entitlementKey(allowedEntitlementSet);
  const icons = useMemo(
    () => getVisibleAnalysisNavIcons(allowedEntitlementSet),
    // entitlementSig is the stable fingerprint; the set object is only read here.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [entitlementSig],
  );

  const shellStyle = {
    width: expanded ? NAV_SIDEBAR_EXPANDED_PX : NAV_SIDEBAR_COLLAPSED_PX,
    background: isDark
      ? 'linear-gradient(180deg, #0b1220 0%, #0a0f1a 60%, #060912 100%)'
      : 'linear-gradient(180deg, #007a39 0%, #006a31 55%, #005a29 100%)',
  } as const;

  const accent = isDark ? '#34d399' : '#34d399';
  const activeBg = isDark ? 'rgba(52,211,153,0.15)' : 'rgba(255,255,255,0.12)';
  const railActiveBg = isDark ? 'rgba(52,211,153,0.15)' : 'rgba(255,255,255,0.14)';

  const itemBase =
    'flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2.5 text-left text-[13px] font-medium leading-[1.4] text-white/90 transition-colors duration-150 hover:bg-white/[0.08] hover:text-white focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-emerald-200 min-h-[44px]';
  const railBase =
    'flex min-h-[58px] w-[58px] shrink-0 flex-col items-center justify-center gap-[5px] rounded-xl text-white/90 transition-colors duration-150 hover:bg-white/[0.08] hover:text-white focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-emerald-200';

  const renderIcon = (id: MapToolRailIcon) => (
    <RailToolIcon
      meta={RAIL_TAB_META[id]}
      active
      theme="dark"
      imgClassName="h-[18px] w-[18px] shrink-0 object-contain"
      iconClassName="text-[18px] leading-none"
      className="opacity-90"
    />
  );

  const settingsIcon = <i className="ri-settings-3-line text-[18px] leading-none opacity-90" aria-hidden />;

  return (
    <aside
      id="app-nav-sidebar"
      aria-label="Main navigation"
      style={shellStyle}
      className="fixed inset-y-0 left-0 z-[70] hidden flex-col text-emerald-50 shadow-[4px_0_24px_rgba(15,23,42,0.06)] transition-[width] duration-200 ease-out motion-reduce:transition-none lg:flex"
    >
      <div className="flex h-20 shrink-0 items-center gap-2.5 border-b-[0.5px] border-white/30 p-[18px]">
        <img
          src={sidebarLogo}
          alt=""
          className="h-9 w-9 shrink-0 rounded-xl bg-white p-1"
          draggable={false}
        />
        {expanded && (
          <div className="min-w-0">
            <span className="block truncate text-lg font-semibold leading-tight tracking-tight text-white">
              Digisaka
            </span>
            <span className="block truncate text-[11px] text-emerald-100/60">
              Agricultural Intelligence Platform
            </span>
          </div>
        )}
      </div>

      <nav
        data-tour-id="map-rail"
        aria-label="Analysis tools"
        className={`nav-sidebar-scroll min-h-0 flex-1 overflow-y-auto overflow-x-hidden ${
          expanded ? 'px-3 py-4' : 'flex flex-col items-center gap-1.5 px-1.5 py-3.5'
        }`}
      >
        {expanded ? (
          <ul className="space-y-1">
            {showCarbonProjects && (
              <li>
                <button
                  type="button"
                  onClick={() => onOpenCarbonProjects?.()}
                  aria-label="Carbon Projects"
                  aria-current={carbonProjectsActive ? 'page' : undefined}
                  className={itemBase}
                  style={
                    carbonProjectsActive
                      ? { background: activeBg, color: '#fff', boxShadow: `inset 3px 0 ${accent}` }
                      : undefined
                  }
                >
                  <i className="ri-leaf-line text-[18px] leading-none opacity-90" aria-hidden />
                  <span className="min-w-0 flex-1">Carbon Projects</span>
                </button>
              </li>
            )}
            {icons.map((id) => {
              const droneChild =
                (id === 'droneData' || id === 'fieldServices') && !!onOpenDroneProcessing;
              const isActive = activeIcon === id || (droneChild && droneProcessingActive);
              const meta = RAIL_TAB_META[id];
              const copy = NAV_ITEM_COPY[id];
              const open =
                (isActive && toolOpen && (subItems.length > 0 || droneChild)) ||
                (droneChild && droneProcessingActive);
              const hasChevron = id !== 'farm' || open;
              const panelId = `nav-sub-${id}`;
              return (
                <li
                  key={id}
                  className="rounded-xl transition-colors duration-150"
                  style={open ? { background: 'rgba(0,0,0,0.09)' } : undefined}
                >
                  <button
                    type="button"
                    onClick={() => onIconClick(id)}
                    aria-label={copy.label}
                    aria-current={isActive ? 'page' : undefined}
                    aria-expanded={hasChevron ? open : undefined}
                    aria-controls={open ? panelId : undefined}
                    title={meta.title ?? copy.label}
                    className={itemBase}
                    style={
                      isActive
                        ? { background: activeBg, color: '#fff', boxShadow: `inset 3px 0 ${accent}` }
                        : undefined
                    }
                  >
                    {renderIcon(id)}
                    <span className="min-w-0 flex-1">{copy.label}</span>
                    {hasChevron && (
                      <i
                        className={`ri-arrow-right-s-line shrink-0 text-sm leading-none opacity-60 transition-transform duration-200 motion-reduce:transition-none ${
                          open ? 'rotate-90' : ''
                        }`}
                        aria-hidden
                      />
                    )}
                  </button>
                  {open && (
                    <ul
                      id={panelId}
                      className="mb-2 ml-[18px] mr-1.5 mt-1 space-y-0.5 border-l-[0.5px] border-white/30 pl-3"
                    >
                      {(id === 'droneData' || id === 'fieldServices') && onOpenDroneProcessing && (
                        <li>
                          <button
                            type="button"
                            onClick={() => {
                              if (DRONE_PROCESSING_COMING_SOON) return;
                              onOpenDroneProcessing();
                            }}
                            aria-label={DRONE_PROCESSING_COMING_SOON ? 'Drone Processing, coming soon' : 'Drone Processing'}
                            aria-disabled={DRONE_PROCESSING_COMING_SOON || undefined}
                            aria-current={!DRONE_PROCESSING_COMING_SOON && droneProcessingActive ? 'true' : undefined}
                            title={DRONE_PROCESSING_COMING_SOON ? 'Coming soon' : 'Drone Processing'}
                            className={`flex w-full min-w-0 items-center gap-2 rounded-md px-2.5 py-[11px] text-left text-[13px] leading-normal transition-colors duration-150 hover:bg-white/[0.08] hover:text-white focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-emerald-200 ${
                              DRONE_PROCESSING_COMING_SOON ? 'cursor-default text-white/50' : droneProcessingActive ? 'font-semibold text-white' : 'text-white/70'
                            }`}
                            style={
                              !DRONE_PROCESSING_COMING_SOON && droneProcessingActive
                                ? { background: 'rgba(255,255,255,0.10)' }
                                : undefined
                            }
                          >
                            <span className="min-w-0 flex-1">Drone Processing</span>
                            {DRONE_PROCESSING_COMING_SOON && (
                              <span className="shrink-0 rounded-full bg-white/20 px-1.5 py-0.5 text-[10px] font-semibold leading-none text-white">
                                Coming soon
                              </span>
                            )}
                          </button>
                        </li>
                      )}
                      {(id === 'droneData' || id === 'fieldServices') &&
                        (subItems.length > 0 || droneProcessingActive) && (
                        <li className="px-2.5 pb-1 pt-2 text-[10px] font-semibold uppercase tracking-[0.08em] text-white/45">
                          Sample Data
                        </li>
                      )}
                      {subItems.map((item, index) => {
                        const subActive = item.id === activeSubItemId;
                        const sensorHeader =
                          id === 'fieldServices' &&
                          isFieldServiceSensorNavSection(item.id) &&
                          !isFieldServiceSensorNavSection(subItems[index - 1]?.id);
                        return (
                          <li key={item.id}>
                            {sensorHeader && (
                              <p className="px-2.5 pb-1 pt-2 text-[10px] font-semibold uppercase tracking-[0.08em] text-white/45">
                                Sensor requests
                              </p>
                            )}
                            <button
                              type="button"
                              onClick={() => {
                                if (subActive && id !== 'farm') onSubItemClose?.();
                                else onSubItemClick?.(item.id);
                              }}
                              aria-current={subActive ? 'true' : undefined}
                              className={`block w-full min-w-0 rounded-md px-2.5 py-[11px] text-left text-[13px] leading-normal transition-colors duration-150 hover:bg-white/[0.08] hover:text-white focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-emerald-200 ${
                                subActive ? 'font-semibold text-white' : 'text-white/70'
                              }`}
                              style={subActive ? { background: 'rgba(255,255,255,0.10)' } : undefined}
                            >
                              {item.label}
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </li>
              );
            })}
            <li>
              <button
                type="button"
                onClick={onOpenSettings}
                aria-label="Settings"
                aria-current={settingsActive ? 'page' : undefined}
                className={itemBase}
                style={
                  settingsActive
                    ? { background: activeBg, color: '#fff', boxShadow: `inset 3px 0 ${accent}` }
                    : undefined
                }
              >
                {settingsIcon}
                <span className="min-w-0 flex-1">Settings</span>
              </button>
            </li>
          </ul>
        ) : (
          <>
            {showCarbonProjects && (
              <button
                type="button"
                onClick={() => onOpenCarbonProjects?.()}
                aria-label="Carbon Projects"
                title="Carbon Projects"
                aria-current={carbonProjectsActive ? 'page' : undefined}
                className={railBase}
                style={
                  carbonProjectsActive
                    ? { background: railActiveBg, color: '#fff', boxShadow: `inset 3px 0 ${accent}` }
                    : undefined
                }
              >
                <i className="ri-leaf-line text-[18px] leading-none" aria-hidden />
                <span className="text-[9px] font-medium leading-[1.3]">Projects</span>
              </button>
            )}
            {icons.map((id) => {
              const isActive = activeIcon === id || (id === 'droneData' && droneProcessingActive);
              const copy = NAV_ITEM_COPY[id];
              return (
                <button
                  key={id}
                  type="button"
                  onClick={() => onIconClick(id)}
                  aria-label={copy.label}
                  aria-current={isActive ? 'page' : undefined}
                  title={copy.label}
                  className={railBase}
                  style={
                    isActive
                      ? { background: railActiveBg, color: '#fff', boxShadow: `inset 3px 0 ${accent}` }
                      : undefined
                  }
                >
                  {renderIcon(id)}
                  <span className="text-[9px] font-medium leading-[1.3]">{copy.short}</span>
                </button>
              );
            })}
            <button
              type="button"
              onClick={onOpenSettings}
              aria-label="Settings"
              title="Settings"
              aria-current={settingsActive ? 'page' : undefined}
              className={railBase}
              style={
                settingsActive
                  ? { background: railActiveBg, color: '#fff', boxShadow: `inset 3px 0 ${accent}` }
                  : undefined
              }
            >
              {settingsIcon}
              <span className="text-[9px] font-medium leading-[1.3]">Settings</span>
            </button>
          </>
        )}
      </nav>

      <div className="shrink-0 border-t-[0.5px] border-white/30 p-3">
        <button
          type="button"
          onClick={onToggleExpanded}
          aria-label={expanded ? 'Collapse navigation' : 'Expand navigation'}
          aria-expanded={expanded}
          aria-controls="app-nav-sidebar"
          className={`flex min-h-[40px] w-full items-center gap-2.5 rounded-[9px] text-xs text-white/90 transition-colors duration-150 hover:bg-white/[0.08] hover:text-white focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-emerald-200 ${
            expanded ? 'justify-start px-3' : 'justify-center'
          }`}
        >
          <i
            className={`ri-arrow-right-s-line text-base leading-none transition-transform duration-200 motion-reduce:transition-none ${
              expanded ? 'rotate-180' : ''
            }`}
            aria-hidden
          />
          {expanded && <span>Collapse sidebar</span>}
        </button>
      </div>
    </aside>
  );
}
